import { useAuditTrail } from '@/hooks/useAdmin';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

const ACTION_LABELS: Record<string, string> = {
  status_change: 'Status Changed',
  tracking_added: 'Tracking Added',
  refund_issued: 'Refund Issued',
  production_updated: 'Production Updated',
  pricing_updated: 'Pricing Updated',
  stock_adjusted: 'Stock Adjusted',
  shipping_updated: 'Shipping Updated',
  config_changed: 'Config Changed',
  note_updated: 'Note Updated',
};

interface AuditTrailProps {
  readonly entityId: string;
}

export const AuditTrail = ({ entityId }: AuditTrailProps) => {
  const { data: trail, isLoading } = useAuditTrail(entityId);

  if (isLoading) {
    return <LoadingSpinner size="sm" />;
  }

  if (!trail || trail.length === 0) {
    return (
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>
        No activity recorded yet.
      </p>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {trail.map((item) => (
        <div
          key={item.auditId}
          style={{
            borderLeft: '2px solid var(--border-default)',
            paddingLeft: 'var(--space-4)',
            paddingBottom: 'var(--space-1)',
          }}
        >
          <div className="flex items-center justify-between">
            <span
              style={{
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text-primary)',
              }}
            >
              {ACTION_LABELS[item.action] ?? item.action}
            </span>
            <span
              style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-tertiary)',
              }}
            >
              {new Date(item.timestamp).toLocaleString()}
            </span>
          </div>
          <p
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--text-tertiary)',
              marginTop: '2px',
            }}
          >
            by {item.actor.startsWith('system') ? item.actor : `User ${item.actor.slice(-8)}`}
          </p>
          {item.changes && Object.keys(item.changes).length > 0 && (
            <div
              style={{
                marginTop: 'var(--space-1)',
                fontSize: 'var(--text-xs)',
                color: 'var(--text-secondary)',
                backgroundColor: 'var(--bg-surface-sunken)',
                padding: 'var(--space-2)',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'monospace',
                overflowX: 'auto',
              }}
            >
              {Object.entries(item.changes).map(([key, value]) => (
                <div key={key}>
                  <span style={{ color: 'var(--text-tertiary)' }}>{key}:</span>{' '}
                  {String(value)}
                </div>
              ))}
            </div>
          )}
          {item.note && (
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: '2px' }}>
              {item.note}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};
